// ============================================================
// LINKED BOOK EDITIONS — single source of truth
//
// Authors can link multiple versions of the SAME story so readers can pick
// the edition that suits their preferences, rather than the platform listing
// them as unrelated books.
//
// The prototype demonstrates this with ONE fictional story, used consistently
// across the book detail page, the public author profile, the author's book
// management area, and discovery.
//
// Key modelling decision: editions belong to a PARENT STORY. The story is the
// catalogue listing (one entry in booksData.ts, one card on the author
// profile, one result in Browse); editions are variants within it. This is
// what keeps a two-edition story from appearing twice everywhere.
// ============================================================

/**
 * What kind of variation an edition represents. This matters to readers:
 * a narrative variant changes the story, a format does not.
 */
export type EditionType =
  | 'content-variant'
  | 'narrative-variant'
  | 'revised-edition'
  | 'format'

export const EDITION_TYPE_LABELS: Record<EditionType, string> = {
  'content-variant': 'Content Variant',
  'narrative-variant': 'Narrative Variant',
  'revised-edition': 'Revised Edition',
  format: 'Format',
}

export const EDITION_TYPE_DESCRIPTIONS: Record<EditionType, string> = {
  'content-variant':
    'Same story and ending, with on-page intensity adjusted — violence, spice or darkness.',
  'narrative-variant':
    'The plot or ending genuinely differs from the original edition.',
  'revised-edition':
    'A re-edited or expanded version of the same story.',
  format:
    'Identical content in a different format, such as paperback or audiobook.',
}

export interface BookEdition {
  id: string
  /** The parent story every edition of this book shares. */
  storyId: string
  name: string
  type: EditionType
  /** Marks the edition shown by default when a reader arrives with no choice. */
  isDefault?: boolean
  coverImage: string
  description: string
  spiceLevel: number
  darknessLevel: number
  endingType: string
  contentWarnings: string[]
  /**
   * Plain-language summary of how this edition differs. Shown before purchase
   * so no reader buys the wrong version by accident.
   */
  whatsDifferent: string
  /** The short tags surfaced on the edition card. */
  editionTags: string[]
  retailerLinks: Array<{ site: string; url: string }>
}

export interface LinkedStory {
  storyId: string
  /** The catalogue listing id — matches the BookCatalogEntry in booksData.ts. */
  bookId: string
  title: string
  author: string
  authorId: string
  editions: BookEdition[]
}

const STORY_ID = 'ember-and-ash'

/**
 * The example story. Two editions of the same romance: one with reduced
 * violence and a gentler tone, one graphic with an alternative ending.
 *
 * Note the editions differ on exactly the axes readers filter by — content
 * warnings, darkness and ending — which is what makes edition-aware discovery
 * demonstrable rather than theoretical.
 */
export const emberAndAshStory: LinkedStory = {
  storyId: STORY_ID,
  bookId: 'ember-and-ash',
  title: 'Ember and Ash',
  author: 'Elena Richards',
  authorId: 'elena-richards',
  editions: [
    {
      id: 'ember-and-ash-light',
      storyId: STORY_ID,
      name: 'Light Edition',
      type: 'content-variant',
      isDefault: true,
      coverImage:
        'https://images.unsplash.com/photo-1490750967868-88aa4486c946?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description:
        'Wrenn Ashby returns to the valley that burned her family name to the ground, and finds the one man who never believed the story. A slow-burning second-chance romance about the long work of forgiveness — told with menace kept firmly off the page.',
      spiceLevel: 3,
      darknessLevel: 2,
      endingType: 'HEA',
      contentWarnings: ['Grief', 'House Fire', 'Estranged Family'],
      whatsDifferent:
        'Violence happens off the page and is referenced rather than shown. The confrontation in the final act resolves without injury, and the couple gets an unambiguous happy ending.',
      editionTags: ['Reduced Violence', 'Lower Darkness', 'HEA'],
      retailerLinks: [
        { site: 'Amazon', url: '#' },
        { site: 'Barnes & Noble', url: '#' },
        { site: 'Kobo', url: '#' },
      ],
    },
    {
      id: 'ember-and-ash-dark',
      storyId: STORY_ID,
      name: 'Dark Edition',
      type: 'narrative-variant',
      coverImage:
        'https://images.unsplash.com/photo-1474932430478-367dbb6832c1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description:
        'Wrenn Ashby returns to the valley that burned her family name to the ground — and this time she intends to find out who lit the match. The same second-chance romance, carried through to the ending the author first wrote: graphic, unsparing, and far less forgiving.',
      spiceLevel: 4,
      darknessLevel: 5,
      endingType: 'Bittersweet',
      contentWarnings: [
        'Graphic Violence',
        'Grief',
        'House Fire',
        'Estranged Family',
        'Revenge',
        'Character Death',
      ],
      whatsDifferent:
        'Violence is shown explicitly, including an extended confrontation in the final act. The third act diverges from the Light Edition and ends bittersweetly rather than happily. Spice is one level higher.',
      editionTags: [
        'Graphic Violence',
        'Higher Darkness',
        'Alternative Ending',
      ],
      retailerLinks: [
        { site: 'Amazon', url: '#' },
        { site: 'Kobo', url: '#' },
      ],
    },
  ],
}

/** Every linked story in the prototype. One, for now. */
export const linkedStories: LinkedStory[] = [emberAndAshStory]

/** The linked story for a catalogue listing, if it has more than one edition. */
export function getLinkedStoryForBook(
  bookId: string | undefined,
): LinkedStory | undefined {
  if (!bookId) return undefined
  const story = linkedStories.find(
    (candidate) =>
      candidate.bookId === bookId ||
      candidate.editions.some((edition) => edition.id === bookId),
  )
  // A single-edition story is not a linked story — callers hide their UI
  // entirely rather than showing a selector with one option in it.
  return story && story.editions.length > 1 ? story : undefined
}

export function getEditionCount(bookId: string | undefined): number {
  if (!bookId) return 0
  const story = linkedStories.find((candidate) => candidate.bookId === bookId)
  return story ? story.editions.length : 0
}

export function getDefaultEdition(story: LinkedStory): BookEdition {
  return story.editions.find((edition) => edition.isDefault) || story.editions[0]
}

export function getEditionById(
  story: LinkedStory,
  editionId: string | null | undefined,
): BookEdition {
  if (!editionId) return getDefaultEdition(story)
  return (
    story.editions.find((edition) => edition.id === editionId) ||
    getDefaultEdition(story)
  )
}

/**
 * Merges an edition's metadata over the parent catalogue entry, so every
 * existing detail-page component keeps reading the same field names it always
 * has and needs no edition awareness of its own.
 */
export function applyEditionToBook<T extends Record<string, any>>(
  book: T,
  edition: BookEdition,
): T {
  return {
    ...book,
    coverImage: edition.coverImage,
    description: edition.description,
    spiceLevel: edition.spiceLevel,
    darknessLevel: edition.darknessLevel,
    endingType: edition.endingType,
    contentWarnings: edition.contentWarnings,
    externalLinks: edition.retailerLinks,
    editionName: edition.name,
  }
}
